/**
 * AppSidebar Component
 *
 * Collapsible navigation sidebar with branding, menu items, and toggle controls.
 *
 * Features:
 * - Redux-controlled visibility state
 * - Unfoldable/narrow mode for more screen space
 * - Brand logo with full and narrow variants
 * - Close button for mobile devices
 * - Footer with toggle button
 * - Dark color scheme
 * - Fixed positioning
 * - Menú dinámico desde el backend (GET /api/menu — la lógica vive en MenuController)
 *
 * @component
 * @example
 * return (
 *   <AppSidebar />
 * )
 */

import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  CCloseButton,
  CSpinner,
  CSidebar,
  CSidebarBrand,
  CSidebarFooter,
  CSidebarHeader,
  CSidebarToggler,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilBuilding,
  cilCalculator,
  cilDescription,
  cilLayers,
  cilMonitor,
  cilChartPie,
  cilChatBubble,
  cilDollar,
  cilGem,
  cilGlobeAlt,
  cilList,
  cilLocationPin,
  cilAccountLogout,
  cilGrid,
  cilPeople,
  cilPrint,
  cilReload,
  cilSettings,
  cilSpeedometer,
  cilStar,
  cilUser,
  cilWallet,
} from '@coreui/icons'
import { CNavGroup, CNavItem } from '@coreui/react'

import { AppSidebarNav } from './AppSidebarNav'
import { getMenu, logout, currentUser } from '../api/client'
import { t } from '../i18n'

const logo = '/design/Resources/logo_siprinia_recortado_original.png'
const sygnet = '/design/Resources/logo_siprinia_recortado_original.png'

const iconosPorEtiqueta = {
  Rutas: cilLocationPin,
  Resumen: cilSpeedometer,
  'Gestión Avanzada': cilGrid,
  'Data Siprin': cilStar,
  Registros: cilList,
  Contabilidad: cilCalculator,
  Informes: cilChartPie,
  Configuración: cilSettings,
  Empresas: cilBuilding,
  Usuarios: cilUser,
  Pagos: cilWallet,
  Impresoras: cilPrint,
  SMS: cilChatBubble,
  Actualizar: cilReload,
  Países: cilGlobeAlt,
  Roles: cilPeople,
  Panel: cilSpeedometer,
  Planes: cilGem,
  'Módulos': cilLayers,
  // [RGM] 2026-09-29 (pedido Rafael: "al submenú Facturación le falta
  // ícono"): grupo Facturación del menú (Categorías/Productos/Facturas).
  'Facturación': cilDescription,
  Sesiones: cilMonitor,
  Actualizaciones: cilReload,
  Salir: cilAccountLogout,
}

const icono = (etiqueta) => {
  const icon = iconosPorEtiqueta[etiqueta]
  return icon ? <CIcon icon={icon} customClassName="nav-icon" /> : null
}

/**
 * Convierte la respuesta del backend ({grupo, items:[{etiqueta,url}]}) al formato
 * que espera AppSidebarNav: {component: CNavItem, name, to} para items sueltos
 * (grupo null) y {component: CNavGroup, name, children} para grupos.
 * Items con url null → /en-desarrollo. "Salir" → logout (no navegación).
 * [RGM] i18n Fase 1: el `name` visible pasa por t() (chrome); el label que no
 * esté en el diccionario queda tal cual (fallback ES — los módulos dinámicos
 * del API NO se traducen por sí solos).
 */
const transformarMenu = (respuesta) =>
  (respuesta?.items ?? []).flatMap((grupo) => {
    const enGrupo = Boolean(grupo.grupo)
    const items = (grupo.items ?? []).map((item) => {
      if (item.etiqueta === 'Salir') {
        return {
          component: CNavItem,
          name: t(item.etiqueta),
          icon: <CIcon icon={cilAccountLogout} customClassName="nav-icon" />,
          onClick: () => logout(),
        }
      }
      return {
        component: CNavItem,
        name: t(item.etiqueta),
        to: item.url ?? '/en-desarrollo',
        // Resumen del superadmin (/superadmin) usa end=true: NavLink matchea
        // por prefijo y sin `end` quedaría activo en TODAS las rutas
        // /superadmin/* (el único item cuyo pathname es prefijo de otros).
        end: item.url === '/superadmin',
        // Los items DENTRO de un grupo (submenú) no llevan icono: AppSidebarNav
        // renderiza el bullet (nav-icon-bullet) para items anidados sin icono,
        // igual que las demás opciones del submenú (Usuarios incluido).
        icon: enGrupo ? undefined : icono(item.etiqueta),
      }
    })
    if (grupo.grupo) {
      // Un grupo con UN SOLO item no necesita submenú (decisión Rafael): se
      // muestra como el item DIRECTO con el icono del grupo y su destino
      // (ej. Informes → /reportes, sin desplegable).
      if (items.length === 1) {
        return [{ ...items[0], icon: icono(grupo.grupo) }]
      }
      return [
        {
          component: CNavGroup,
          name: t(grupo.grupo),
          icon: icono(grupo.grupo),
          items,
        },
      ]
    }
    return items
  })

/**
 * AppSidebar functional component
 *
 * Manages sidebar state with Redux:
 * - sidebarShow: Controls sidebar visibility
 * - sidebarUnfoldable: Controls narrow/wide mode
 *
 * Carga el menú desde el backend (getMenu) al montar; mientras tanto muestra un
 * spinner. Memoized to prevent unnecessary re-renders.
 *
 * @returns {React.ReactElement} Sidebar with navigation
 */
const AppSidebar = () => {
  const dispatch = useDispatch()
  const unfoldable = useSelector((state) => state.sidebarUnfoldable)
  const sidebarShow = useSelector((state) => state.sidebarShow)
  const [nav, setNav] = useState([])
  const [cargando, setCargando] = useState(true)

  // Nombre real completo del usuario (UsuarioNombre) — NO el usuario de login
  // ni el email. Se muestra en el footer, misma fila que el toggler. El
  // char(n) del esquema GX llega con padding: se recorta con trim().
  const nombreCompleto = (currentUser()?.name ?? '').trim()

  useEffect(() => {
    let cancelado = false
    getMenu()
      .then((respuesta) => {
        if (!cancelado) setNav(transformarMenu(respuesta))
      })
      .catch(() => {
        // 401 ya redirige a /login (client.ts); otros errores → menú vacío
      })
      .finally(() => {
        if (!cancelado) setCargando(false)
      })
    return () => {
      cancelado = true
    }
  }, [])

  return (
    <CSidebar
      className="border-end"
      colorScheme="dark"
      position="fixed"
      unfoldable={unfoldable}
      visible={sidebarShow}
      onVisibleChange={(visible) => {
        dispatch({ type: 'set', sidebarShow: visible })
      }}
    >
      <CSidebarHeader className="border-bottom">
        <CSidebarBrand to="/" className="text-decoration-none">
          <img src={logo} alt="SiprinIA" height={32} className="sidebar-brand-full" />
          <span className="sidebar-brand-full ms-2 fw-bold">SiprinIA</span>
          <img src={sygnet} alt="SiprinIA" height={32} className="sidebar-brand-narrow" />
        </CSidebarBrand>
        <CCloseButton
          className="d-lg-none"
          dark
          onClick={() => dispatch({ type: 'set', sidebarShow: false })}
        />
      </CSidebarHeader>
      {cargando ? (
        <div className="text-center py-4">
          <CSpinner color="light" size="sm" />
        </div>
      ) : (
        <AppSidebarNav items={nav} />
      )}
      <CSidebarFooter
        className="border-top d-none d-lg-flex align-items-center"
        // Padding vertical 12px: iguala la altura del footer del contenido
        // (CFooter ~49px con texto centrado) para que la linea del nombre
        // quede en la misma linea que el texto del footer de la pagina.
        style={{ paddingTop: 12, paddingBottom: 12 }}
      >
        <div
          className="sidebar-brand-full flex-grow-1 text-truncate ps-3"
          title={nombreCompleto}
        >
          {/* Mismo estilo visual del toggler: color --cui-tertiary-color (el
              toggler usa esa misma variable, ver sidebar _variables.scss),
              grosor normal (letra delgada), no semibold ni blanco brillante. */}
          <span className="small" style={{ color: 'var(--cui-tertiary-color)' }}>{nombreCompleto}</span>
        </div>
        <CSidebarToggler
          onClick={() => dispatch({ type: 'set', sidebarUnfoldable: !unfoldable })}
        />
      </CSidebarFooter>
    </CSidebar>
  )
}

export default React.memo(AppSidebar)
